import { computed, effect, Injectable, signal } from "@angular/core";
import { getFontEmbedCSS, toCanvas } from "html-to-image";

// How a screenshot is laid out before it is captured:
//  - slide:  ("Consistent" in the UI) the element is re-laid out at the width that gives it the slide shape (by default
//            the 25.4 x 12 cm area under a slide title) for its current height, then exported
//            at a fixed pixel size
//  - screen: captured as it is on screen (depends on window size, zoom and display scaling)
// In slide mode the output only depends on the settings (viewer height etc.), not on the
// window size, browser zoom or display of the machine it was taken on.
export type ScreenshotFrameMode = "slide" | "screen";

// the image area on the slide, in cm (Google Slides / PowerPoint widescreen is 25.4 x 14.29)
export interface SlideShape {
  widthCm: number;
  heightCm: number;
}
export const SLIDE_SHAPE_PRESETS: (SlideShape & { id: string; label: string; shortLabel: string })[] = [
  { id: "body", label: "Below title (25.4 × 12 cm)", shortLabel: "Below title", widthCm: 25.4, heightCm: 12 },
  { id: "full", label: "Full slide, 16:9 (25.4 × 14.29 cm)", shortLabel: "Full 16:9", widthCm: 25.4, heightCm: 14.29 },
];
// output pixel width of slide captures (the height follows from the shape) — plenty for a
// full-width slide image; higher widths looked no different and just cost encode time
const SLIDE_OUTPUT_WIDTH = 2560;

export interface FrameCaptureOptions {
  filter?: (node: HTMLElement) => boolean;
  backgroundColor?: string;
  // fire a window resize event after changing the width, for components that size
  // themselves from @HostListener('window:resize') rather than a ResizeObserver
  notifyWindowResize?: boolean;
  // extra readiness check run after the layout has stopped changing (e.g. data loading)
  waitForReady?: () => Promise<void>;
  // wraps the html-to-image call (used by the batch runner to keep rAF ticking in background tabs)
  wrapCapture?: <T>(work: () => Promise<T>) => Promise<T>;
  // how long the layout must stay unchanged after the re-frame before capturing; pages whose
  // plots redraw on debounced window-resize handlers need a window longer than those debounces
  settle?: { minWaitMs: number; stableMs: number };
}

const STORAGE_KEY = "nebula.screenshotFrame";
const MIN_FRAME_WIDTH = 400;
const MAX_FRAME_WIDTH = 4000;

// While `capturing` is set the model viewers skip their resize debounce and transitions, so
// the re-framed layout lands within a few frames; this only has to outlast those frames.
// Pages with their own debounced redraws pass a longer `settle` window.
const DEFAULT_SETTLE = { minWaitMs: 100, stableMs: 250 };
const LAYOUT_TIMEOUT_MS = 10_000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

@Injectable({ providedIn: "root" })
export class ScreenshotFrameService {
  readonly mode = signal<ScreenshotFrameMode>("slide");
  readonly slideWidth = SLIDE_OUTPUT_WIDTH;
  readonly slideShape = signal<SlideShape>({ widthCm: 25.4, heightCm: 12 });
  // "custom" is sticky once picked, so the cm inputs stay open even while they happen to match a preset
  private customShapePicked = signal(false);
  // id of the preset matching the current shape, or "custom"
  readonly slideShapeId = computed(() => {
    const { widthCm, heightCm } = this.slideShape();
    const preset = SLIDE_SHAPE_PRESETS.find((p) => p.widthCm === widthCm && p.heightCm === heightCm);
    return this.customShapePicked() || !preset ? "custom" : preset.id;
  });
  readonly slideAspect = computed(() => this.slideShape().widthCm / this.slideShape().heightCm);
  readonly slideHeight = computed(() => Math.round(this.slideWidth / this.slideAspect()));

  // true while a re-framed capture is in progress — viewers read this to redraw immediately
  // (no resize debounce / animation). Plain field, read at event time, so effects don't track it.
  capturing = false;

  // @font-face CSS that html-to-image embeds; building it walks every stylesheet on the page
  // (and fetches any it can't read), so do it once per captured element rather than per capture
  private fontCssCache = new Map<string, Promise<string>>();

  constructor() {
    this.load();
    effect(() => this.save());
  }

  selectSlideShape(id: string) {
    this.customShapePicked.set(id === "custom");
    const preset = SLIDE_SHAPE_PRESETS.find((p) => p.id === id);
    if (preset) this.slideShape.set({ widthCm: preset.widthCm, heightCm: preset.heightCm });
  }

  // custom shape edits; ignores empty / non-positive input mid-typing
  setSlideShapeSize(dimension: "widthCm" | "heightCm", value: number) {
    if (!(value > 0)) return;
    this.slideShape.update((shape) => ({ ...shape, [dimension]: Math.round(value * 100) / 100 }));
  }

  // short tag for file names so captures taken with different frames are distinguishable
  fileTag(): string {
    return this.mode() === "slide" ? `-S${this.slideShape().widthCm}x${this.slideShape().heightCm}cm` : "";
  }

  async capture(element: HTMLElement, options: FrameCaptureOptions = {}): Promise<string> {
    const wrap = options.wrapCapture ?? (<T>(work: () => Promise<T>) => work());
    const background = options.backgroundColor ?? "#ffffff";
    const render = async (pixelRatio: number) => {
      const fontEmbedCSS = await this.fontEmbedCss(element);
      return wrap(() =>
        toCanvas(element, {
          pixelRatio,
          filter: options.filter,
          backgroundColor: background,
          fontEmbedCSS,
        }),
      );
    };

    const mode = this.mode();
    if (mode === "screen") return (await render(window.devicePixelRatio)).toDataURL("image/png");

    const settle = options.settle ?? DEFAULT_SETTLE;
    const original = {
      width: element.style.width,
      minWidth: element.style.minWidth,
      maxWidth: element.style.maxWidth,
      flex: element.style.flex,
    };
    const setWidth = async (width: number) => {
      element.style.width = `${width}px`;
      element.style.minWidth = `${width}px`;
      element.style.maxWidth = "none";
      element.style.flex = "none";
      if (options.notifyWindowResize) window.dispatchEvent(new Event("resize"));
      await this.waitForLayoutSettled(element, settle);
      await options.waitForReady?.();
    };

    // the viewers re-lay themselves out through ResizeObservers and d3 transitions, neither
    // of which run in a hidden tab — so a re-framed capture has to wait for the tab
    await whenTabVisible();

    this.capturing = true;
    try {
      // slide: the height is driven by the viewer height settings and barely depends on
      // the width, so converge on width = height * aspect (usually a single pass)
      const aspect = this.slideAspect();
      let width = clampWidth(Math.round(element.offsetHeight * aspect));
      for (let pass = 0; pass < 3; pass++) {
        await setWidth(width);
        const next = clampWidth(Math.round(element.offsetHeight * aspect));
        if (Math.abs(next - width) <= 2) break;
        width = next;
      }
      const outWidth = this.slideWidth;
      const canvas = await render(outWidth / element.offsetWidth);
      // composed straight from the canvas so the (slow, at this size) PNG encode happens once
      return fitToCanvas(canvas, outWidth, Math.round(outWidth / aspect), background).toDataURL("image/png");
    } finally {
      this.capturing = false;
      element.style.width = original.width;
      element.style.minWidth = original.minWidth;
      element.style.maxWidth = original.maxWidth;
      element.style.flex = original.flex;
      if (options.notifyWindowResize) window.dispatchEvent(new Event("resize"));
    }
  }

  private fontEmbedCss(element: HTMLElement): Promise<string> {
    const key = element.id || element.tagName;
    let css = this.fontCssCache.get(key);
    if (!css) {
      css = getFontEmbedCSS(element).catch((err) => {
        // don't cache a failure (e.g. a stylesheet fetch blip) — retry next capture
        this.fontCssCache.delete(key);
        throw err;
      });
      this.fontCssCache.set(key, css);
    }
    return css;
  }

  // Polls a cheap fingerprint of the element's geometry (its own box plus every svg,
  // canvas and img inside it) until it has not changed for `settle.stableMs`.
  private async waitForLayoutSettled(element: HTMLElement, settle: { minWaitMs: number; stableMs: number }) {
    await delay(settle.minWaitMs);
    const start = Date.now();
    let last = "";
    let stableSince = Date.now();
    while (Date.now() - start < LAYOUT_TIMEOUT_MS) {
      const current = layoutFingerprint(element);
      if (current !== last) {
        last = current;
        stableSince = Date.now();
      } else if (Date.now() - stableSince >= settle.stableMs) {
        return;
      }
      await delay(50);
    }
  }

  private load() {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
      // (an old "fixed" setting falls back to the slide default)
      if (["slide", "screen"].includes(data.mode)) this.mode.set(data.mode);
      if (data.slideShape?.widthCm > 0 && data.slideShape?.heightCm > 0) {
        this.slideShape.set({ widthCm: data.slideShape.widthCm, heightCm: data.slideShape.heightCm });
      }
    } catch {
      // corrupt / unavailable storage: keep the defaults
    }
  }

  private save() {
    const data = { mode: this.mode(), slideShape: this.slideShape() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // storage unavailable: settings just won't persist
    }
  }
}

function whenTabVisible(): Promise<void> {
  if (!document.hidden) return Promise.resolve();
  return new Promise((resolve) => {
    const onVisible = () => {
      if (!document.hidden) {
        document.removeEventListener("visibilitychange", onVisible);
        resolve();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
  });
}

function clampWidth(width: number): number {
  return Math.max(MIN_FRAME_WIDTH, Math.min(MAX_FRAME_WIDTH, Math.round(width)));
}

function layoutFingerprint(element: HTMLElement): string {
  const parts = [element.offsetWidth, element.offsetHeight];
  element.querySelectorAll("svg, canvas, img").forEach((node) => {
    const rect = node.getBoundingClientRect();
    parts.push(Math.round(rect.width), Math.round(rect.height));
    // d3 animates the inner image/axes, which doesn't always move the outer box
    if (node instanceof SVGSVGElement) {
      node.querySelectorAll("image").forEach((img) => parts.push(Number(img.getAttribute("width"))));
    }
  });
  return parts.join(",");
}

// Draws the capture centred on an exact width x height canvas (scaled down to fit if
// needed), so every slide capture has identical pixel dimensions.
function fitToCanvas(source: HTMLCanvasElement, width: number, height: number, background: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);
  const scale = Math.min(width / source.width, height / source.height, 1);
  const w = source.width * scale;
  const h = source.height * scale;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, (width - w) / 2, (height - h) / 2, w, h);
  return canvas;
}
